<script setup>
import { ref, watch, computed, onMounted } from 'vue';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    options: {
        type: Array,
        required: true
    },
    optionLabel: {
        type: String,
        default: 'label'
    },
    optionValue: {
        type: String,
        default: 'value'
    },
    title: {
        type: String,
        default: null
    },
    searchPlaceholder: {
        type: String,
        default: 'Search...'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    maxHeight: {
        type: String,
        default: '400px'
    },
    dataTestId: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'open']);

const searchQuery = ref('');
const selectedOptions = ref(props.modelValue || []);

watch(() => props.modelValue, (newValue) => {
    selectedOptions.value = newValue || [];
}, { deep: true });

const filteredOptions = computed(() => {
    if (!searchQuery.value || !searchQuery.value.trim()) {
        return props.options;
    }
    
    return props.options.filter(option => {
        if (!option || option[props.optionLabel] === undefined || option[props.optionLabel] === null) return false;
        
        const label = normalizeText(String(option[props.optionLabel]));
        const query = normalizeText(searchQuery.value);
        return label.includes(query);
    });
});

const normalizeText = (text) => {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '')
        .trim();
};
const isSelected = (optionValue) => {
    return selectedOptions.value.includes(optionValue);
};

const isOptionDisabled = (option) => {
    return props.disabled || props.loading || (option && option.disabled === true);
};

const toggleOption = (optionValue) => {
    if (props.disabled || props.loading) return;
    
    const option = filteredOptions.value.find(opt => opt[props.optionValue] === optionValue) 
        || props.options.find(opt => opt[props.optionValue] === optionValue);
    if (isOptionDisabled(option)) return;
    
    const index = selectedOptions.value.indexOf(optionValue);
    if (index > -1) {
        selectedOptions.value.splice(index, 1);
    } else {
        selectedOptions.value.push(optionValue);
    }
    
    emit('update:modelValue', [...selectedOptions.value]);
};

const isAllSelected = computed(() => {
    if (filteredOptions.value.length === 0) return false;
    const enabledOptions = filteredOptions.value.filter(opt => !isOptionDisabled(opt));
    if (enabledOptions.length === 0) return false;
    return enabledOptions.every(option => 
        selectedOptions.value.includes(option[props.optionValue])
    );
});

const isSomeSelected = computed(() => {
    if (filteredOptions.value.length === 0) return false;
    const enabledOptions = filteredOptions.value.filter(opt => !isOptionDisabled(opt));
    if (enabledOptions.length === 0) return false;
    return enabledOptions.some(option => 
        selectedOptions.value.includes(option[props.optionValue])
    ) && !isAllSelected.value;
});

const toggleSelectAll = () => {
    if (props.disabled || props.loading) return;
    
    const enabledOptions = filteredOptions.value.filter(opt => !isOptionDisabled(opt));
    const enabledValues = enabledOptions.map(opt => opt[props.optionValue]);
    
    if (isAllSelected.value) {
        selectedOptions.value = selectedOptions.value.filter(val => !enabledValues.includes(val));
    } else {
        const newSelected = new Set([...selectedOptions.value, ...enabledValues]);
        selectedOptions.value = Array.from(newSelected);
    }
    
    emit('update:modelValue', [...selectedOptions.value]);
};

onMounted(() => {
    if (props.options.length === 0) {
        emit('open');
    }
});
</script>

<template>
    <div class="checkbox-list overflow-y-auto" :data-testid="dataTestId">
        <!-- Header -->
        <div v-if="title !== null" class="checkbox-list__header">
            <h3 class="checkbox-list__title">{{ title }}</h3>
        </div>

        <!-- Search Input -->
        <div class="checkbox-list__search-wrapper" :class="{ 'checkbox-list__search-wrapper--loading': loading }">
            <input
                v-model="searchQuery"
                type="text"
                class="checkbox-list__search"
                :placeholder="searchPlaceholder"
                :disabled="disabled || loading"
            />
            <i class="uil uil-search checkbox-list__search-icon"></i>
        </div>

        <!-- Options List -->
        <div 
            class="checkbox-list__options"
            :style="{ maxHeight: maxHeight }"
        >
            <div v-if="loading" class="checkbox-list__loader">
                <div class="checkbox-list__spinner"></div>
            </div>
            <template v-else-if="filteredOptions.length > 0">
                <label
                    v-for="(option, index) in filteredOptions"
                    :key="`${index}-${option[optionValue]}`"
                    class="checkbox-list__option"
                    :class="{ 
                        'checkbox-list__option--selected': isSelected(option[optionValue]),
                        'checkbox-list__option--disabled': disabled || isOptionDisabled(option)
                    }"
                >
                    <span class="checkbox-list__option-label">
                        {{ option[optionLabel] }}
                    </span>
                    <span 
                        v-if="option.status" 
                        class="inline-flex items-center px-2 mr-8 py-1 text-xs font-medium rounded border"
                        :title="option.status"
                        :class="{ 'text-yellow-400 bg-yellow-600/20 border-yellow-600/30': option.status === 'pending','text-blue-400 bg-blue-600/20 border-blue-600/30': option.status === 'active', 'text-green-400 bg-green-600/20 border-green-600/30': option.status === 'completed', 'text-red-400 bg-red-600/20 border-red-600/30': option.status === 'failed' }"
                    >
                        <i
                            :class="{ 'mdi mdi-clock-outline mr-1 text-xs': option.status === 'pending', 'mdi mdi-clock-outline mr-1 text-xs': option.status === 'active', 'mdi mdi-check-circle-outline mr-1 text-xs': option.status === 'completed', 'mdi mdi-close-circle-outline mr-1 text-xs': option.status === 'failed' }"
                        ></i>
                        {{ option.status }}
                    </span>
                    <input
                        type="checkbox"
                        class="checkbox-list__checkbox"
                        :checked="isSelected(option[optionValue])"
                        :disabled="disabled || isOptionDisabled(option)"
                        @change="toggleOption(option[optionValue])"
                    />
                </label>
            </template>
            <div v-else class="checkbox-list__no-options">
                No options found
            </div>
        </div>

        <!-- Select All -->
        <div class="checkbox-list__footer" :class="{ 'checkbox-list__footer--loading': loading }">
            <label
                class="checkbox-list__select-all"
                :class="{ 'checkbox-list__select-all--disabled': disabled || loading || filteredOptions.length === 0 || filteredOptions.every(opt => isOptionDisabled(opt)) }"
            >
                <span class="checkbox-list__select-all-label">Select All</span>
                <input
                    type="checkbox"
                    class="checkbox-list__checkbox checkbox-list__checkbox--select-all"
                    :checked="isAllSelected"
                    :indeterminate.prop="isSomeSelected"
                    :disabled="disabled || loading || filteredOptions.length === 0 || filteredOptions.every(opt => isOptionDisabled(opt))"
                    @change="toggleSelectAll"
                />
            </label>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.checkbox-list {
    @apply w-full bg-echoes-background-black rounded-lg border border-echoes-border-light overflow-hidden;
}

.checkbox-list__header {
    @apply px-4 py-3 border-b border-gray-700;
}

.checkbox-list__title {
    @apply text-base font-semibold text-white m-0;
}

.checkbox-list__search-wrapper {
    @apply relative px-4 py-3 border-b border-gray-700;
}

.checkbox-list__search-wrapper--loading {
    @apply opacity-50;
}

.checkbox-list__search {
    @apply w-full px-4 py-2 pr-10 bg-gray-800 border border-gray-600 rounded-lg
        text-white placeholder-gray-400
        focus:outline-none focus:ring-2 focus:ring-echoes-background-black focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed;
}

.checkbox-list__search-icon {
    @apply absolute right-7 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none;
}

.checkbox-list__options {
    @apply overflow-y-auto;
    scrollbar-width: thin;
    scrollbar-color: echoes-background-gray transparent;
}

.checkbox-list__options::-webkit-scrollbar {
    width: 6px;
}

.checkbox-list__options::-webkit-scrollbar-track {
    background: transparent;
}

.checkbox-list__options::-webkit-scrollbar-thumb {
    background-color: echoes-background-gray;
    border-radius: 3px;
    transition: background-color 0.3s ease;
}

.checkbox-list__options::-webkit-scrollbar-thumb:hover {
    background-color: echoes-background-black;
}

.checkbox-list__option {
    @apply flex items-center justify-between px-4 py-3 cursor-pointer
        transition-colors duration-200
        hover:bg-gray-800
        border-b border-gray-800 last:border-b-0;
}

.checkbox-list__option--selected {
    @apply bg-gray-800/50;
}

.checkbox-list__option--disabled {
    @apply opacity-50 cursor-not-allowed hover:bg-transparent;
}

.checkbox-list__option-label {
    @apply text-sm text-white flex-1 pr-4;
}

.checkbox-list__checkbox {
    @apply w-5 h-5 rounded border-2 border-gray-600 bg-gray-800
        cursor-pointer
        checked:bg-echoes-background-black checked:border-echoes-background-black
        focus:outline-none focus:ring-2 focus:ring-echoes-background-black focus:ring-offset-2 focus:ring-offset-gray-900
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200;
}

.checkbox-list__checkbox:indeterminate {
    @apply bg-echoes-background-black;
}

.checkbox-list__no-options {
    @apply px-4 py-8 text-center text-sm text-gray-400;
}

.checkbox-list__loader {
    @apply flex items-center justify-center py-12;
}

.checkbox-list__spinner {
    @apply w-8 h-8;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top: 3px solid #FE3E01;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.checkbox-list__footer {
    @apply px-4 py-3 border-t border-gray-700 bg-gray-900;
}

.checkbox-list__footer--loading {
    @apply opacity-50;
}

.checkbox-list__select-all {
    @apply flex items-center justify-between cursor-pointer;
}

.checkbox-list__select-all--disabled {
    @apply opacity-50 cursor-not-allowed;
}

.checkbox-list__select-all-label {
    @apply text-sm font-medium text-white;
}

.checkbox-list__checkbox--select-all {
    @apply w-5 h-5;
}

.overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: echoes-background-gray transparent;
}

.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: echoes-background-gray;
    border-radius: 3px;
    transition: background-color 0.3s ease;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: echoes-background-black;
}
</style>
